import { Amount, round, VaultInfo, VAULTS } from '@kunalabs-io/kai'
import { PhantomTypeArgument } from '@/shared/gen/_framework/reified'
import { IconProps } from 'components/Icons/Base'
import { IconSUI } from 'components/Icons/IconSUI'
import { IconUSDC } from 'components/Icons/IconUSDC'
import IconUSDT from 'components/Icons/IconUSDT'
import React from 'react'

export interface VaultDisplayInfo {
  vault: VaultInfo<PhantomTypeArgument, PhantomTypeArgument>
  tSymbol: string
  ytSymbol: string
  icon: React.ComponentType<IconProps>
  formatAmount: (amount: Amount | number | undefined) => string | undefined
  strategyDescription: string
}

function formatAmountToTokenDisplay(symbol: string) {
  return function (amount?: Amount | number) {
    if (amount === undefined) {
      return undefined
    }

    const amountNum = typeof amount === 'number' ? amount : amount.toNumber()
    if (amountNum > 0 && amountNum < 0.01) {
      return `<0.01 ${symbol}`
    }

    if (amountNum > -0.00001 && amountNum < 0) {
      return `~0 ${symbol}`
    }
    if (Math.abs(amountNum) >= 1_000_000) {
      const millions = round(amountNum / 1_000_000, 2)
      return `${millions.toLocaleString('en-US', { maximumFractionDigits: 2 })}M` + ' ' + symbol
    } else if (Math.abs(amountNum) >= 10_000) {
      const thousands = round(amountNum / 1_000, 2)
      return `${thousands.toLocaleString('en-US', { maximumFractionDigits: 2 })}K` + ' ' + symbol
    } else {
      return amountNum.toLocaleString('en-US', { maximumFractionDigits: 2 }) + ' ' + symbol
    }
  }
}

export const vaultDisplayInfoList: VaultDisplayInfo[] = [
  {
    vault: VAULTS.wUSDC as VaultInfo<PhantomTypeArgument, PhantomTypeArgument>,
    tSymbol: VAULTS.wUSDC.T.displaySymbol,
    ytSymbol: VAULTS.wUSDC.YT.displaySymbol,
    icon: IconUSDC,
    formatAmount: formatAmountToTokenDisplay(VAULTS.wUSDC.T.displaySymbol),
    strategyDescription: 'strategyDescUSDC',
  },
  {
    vault: VAULTS.wUSDT as VaultInfo<PhantomTypeArgument, PhantomTypeArgument>,
    tSymbol: VAULTS.wUSDT.T.displaySymbol,
    ytSymbol: VAULTS.wUSDT.YT.displaySymbol,
    icon: IconUSDT,
    formatAmount: formatAmountToTokenDisplay(VAULTS.wUSDT.T.displaySymbol),
    strategyDescription: 'strategyDescUSDT',
  },
  {
    vault: VAULTS.SUI as VaultInfo<PhantomTypeArgument, PhantomTypeArgument>,
    tSymbol: VAULTS.SUI.T.displaySymbol,
    ytSymbol: VAULTS.SUI.YT.displaySymbol,
    icon: IconSUI,
    formatAmount: formatAmountToTokenDisplay(VAULTS.SUI.T.displaySymbol),
    strategyDescription: 'strategyDescSUI',
  },
  {
    vault: VAULTS.USDY as VaultInfo<PhantomTypeArgument, PhantomTypeArgument>,
    tSymbol: VAULTS.USDY.T.displaySymbol,
    ytSymbol: VAULTS.USDY.YT.displaySymbol,
    icon: () => <img src={VAULTS.USDY.T.iconUrl} alt="USDY" width={33} height={33} />,
    formatAmount: formatAmountToTokenDisplay(VAULTS.USDY.T.displaySymbol),
    strategyDescription: 'strategyDescUSDC',
  },
  {
    vault: VAULTS.DEEP as VaultInfo<PhantomTypeArgument, PhantomTypeArgument>,
    tSymbol: VAULTS.DEEP.T.displaySymbol,
    ytSymbol: VAULTS.DEEP.YT.displaySymbol,
    icon: () => <img src={VAULTS.DEEP.T.iconUrl} alt="DEEP" width={33} height={33} />,
    formatAmount: formatAmountToTokenDisplay(VAULTS.DEEP.T.displaySymbol),
    strategyDescription: 'strategyDescDEEP',
  },
  {
    vault: VAULTS.USDC as VaultInfo<PhantomTypeArgument, PhantomTypeArgument>,
    tSymbol: VAULTS.USDC.T.displaySymbol,
    ytSymbol: VAULTS.USDC.YT.displaySymbol,
    icon: IconUSDC,
    formatAmount: formatAmountToTokenDisplay(VAULTS.USDC.T.displaySymbol),
    strategyDescription: 'strategyDescUSDC',
  },
  {
    vault: VAULTS.suiUSDT as VaultInfo<PhantomTypeArgument, PhantomTypeArgument>,
    tSymbol: VAULTS.suiUSDT.T.displaySymbol,
    ytSymbol: VAULTS.suiUSDT.YT.displaySymbol,
    icon: IconUSDT,
    formatAmount: formatAmountToTokenDisplay(VAULTS.suiUSDT.T.displaySymbol),
    strategyDescription: 'strategyDescUSDT',
  },
  {
    vault: VAULTS.WAL as VaultInfo<PhantomTypeArgument, PhantomTypeArgument>,
    tSymbol: VAULTS.WAL.T.displaySymbol,
    ytSymbol: VAULTS.WAL.YT.displaySymbol,
    icon: () => <img src={VAULTS.WAL.T.iconUrl} alt="WAL" width={33} height={33} style={{ borderRadius: '50%' }} />,
    formatAmount: formatAmountToTokenDisplay(VAULTS.WAL.T.displaySymbol),
    strategyDescription: 'strategyDescUSDT',
  },
  {
    vault: VAULTS.wBTC as VaultInfo<PhantomTypeArgument, PhantomTypeArgument>,
    tSymbol: VAULTS.wBTC.T.displaySymbol,
    ytSymbol: VAULTS.wBTC.YT.displaySymbol,
    icon: () => <img src={VAULTS.wBTC.T.iconUrl} alt="wBTC" width={33} height={33} />,
    formatAmount: formatAmountToTokenDisplay(VAULTS.wBTC.T.displaySymbol),
    strategyDescription: 'strategyDescUSDT',
  },
  {
    vault: VAULTS.LBTC as VaultInfo<PhantomTypeArgument, PhantomTypeArgument>,
    tSymbol: VAULTS.LBTC.T.displaySymbol,
    ytSymbol: VAULTS.LBTC.YT.displaySymbol,
    icon: () => <img src={VAULTS.LBTC.T.iconUrl} alt="LBTC" width={33} height={33} />,
    formatAmount: formatAmountToTokenDisplay(VAULTS.LBTC.T.displaySymbol),
    strategyDescription: 'strategyDescUSDT',
  },
  {
    vault: VAULTS.xBTC as VaultInfo<PhantomTypeArgument, PhantomTypeArgument>,
    tSymbol: VAULTS.xBTC.T.displaySymbol,
    ytSymbol: VAULTS.xBTC.YT.displaySymbol,
    icon: () => <img src={VAULTS.xBTC.T.iconUrl} alt="xBTC" width={33} height={33} />,
    formatAmount: formatAmountToTokenDisplay(VAULTS.xBTC.T.displaySymbol),
    strategyDescription: 'strategyDescUSDT',
  },
  {
    vault: VAULTS.USDSUI as VaultInfo<PhantomTypeArgument, PhantomTypeArgument>,
    tSymbol: VAULTS.USDSUI.T.displaySymbol,
    ytSymbol: VAULTS.USDSUI.YT.displaySymbol,
    icon: () => (
      <img src={VAULTS.USDSUI.T.iconUrl} alt="USDSUI" width={33} height={33} style={{ borderRadius: '50%' }} />
    ),
    formatAmount: formatAmountToTokenDisplay(VAULTS.USDSUI.T.displaySymbol),
    strategyDescription: 'strategyDescUSDT',
  },
]

export const frozenVaults: VaultDisplayInfo[] = [
  {
    vault: VAULTS.paused_USDC as VaultInfo<PhantomTypeArgument, PhantomTypeArgument>,
    tSymbol: VAULTS.paused_USDC.T.displaySymbol,
    ytSymbol: VAULTS.paused_USDC.YT.displaySymbol,
    icon: IconUSDC,
    formatAmount: formatAmountToTokenDisplay(VAULTS.paused_USDC.T.displaySymbol),
    strategyDescription: 'strategyDescUSDC',
  },
  {
    vault: VAULTS.paused_suiUSDT as VaultInfo<PhantomTypeArgument, PhantomTypeArgument>,
    tSymbol: VAULTS.paused_suiUSDT.T.displaySymbol,
    ytSymbol: VAULTS.paused_suiUSDT.YT.displaySymbol,
    icon: IconUSDT,
    formatAmount: formatAmountToTokenDisplay(VAULTS.paused_suiUSDT.T.displaySymbol),
    strategyDescription: 'strategyDescUSDT',
  },
]

export const recommendedVaults: VaultDisplayInfo[] = [
  {
    vault: VAULTS.USDC as VaultInfo<PhantomTypeArgument, PhantomTypeArgument>,
    tSymbol: VAULTS.USDC.T.displaySymbol,
    ytSymbol: VAULTS.USDC.YT.displaySymbol,
    icon: IconUSDC,
    formatAmount: formatAmountToTokenDisplay(VAULTS.USDC.T.displaySymbol),
    strategyDescription: 'strategyDescUSDC',
  },
  {
    vault: VAULTS.SUI as VaultInfo<PhantomTypeArgument, PhantomTypeArgument>,
    tSymbol: VAULTS.SUI.T.displaySymbol,
    ytSymbol: VAULTS.SUI.YT.displaySymbol,
    icon: IconSUI,
    formatAmount: formatAmountToTokenDisplay(VAULTS.SUI.T.displaySymbol),
    strategyDescription: 'strategyDescSUI',
  },
]

export function getVaultDisplayInfoFromSymbol(symbol: string) {
  for (const vaultDisplayInfo of vaultDisplayInfoList) {
    if (vaultDisplayInfo.tSymbol === symbol.toUpperCase()) {
      return vaultDisplayInfo
    }
  }

  throw new Error(`Unknown vault symbol: ${symbol}`)
}
